import { useCallback, useEffect, useRef } from 'react';
import type { ColorScheme } from '@cbhq/cds-common/core/theme';
import { ActionType, ComponentType } from '@cbhq/client-analytics';

import type { Application } from '../providers/Applications.types';

import { useAnalytics } from './useAnalytics';
import { applicationSourceImage } from './useApplicationSourceImage';

export function useCacheIllustrations(spectrum: ColorScheme, apps?: Application[]) {
  const { logEvent } = useAnalytics();
  const hasPreloaded = useRef(false);

  const getSourceImage = useCallback(
    (application: Application): string => {
      return applicationSourceImage(spectrum, application) ?? '';
    },
    [spectrum],
  );

  const logImageError = useCallback(
    ({ applicationId, illustrationUrl, tileName }: Application, error?: unknown) => {
      logEvent('error_preloading_illustrations', {
        action: ActionType.process,
        componentType: ComponentType.icon,
        application: applicationId,
        url: illustrationUrl,
        tileName,
        error: error && error instanceof Error ? error.message : String(error),
      });
    },
    [logEvent],
  );

  useEffect(() => {
    if (apps && !hasPreloaded.current) {
      apps.forEach((app) => {
        try {
          const img = new Image();
          img.src = getSourceImage(app);
          img.onerror = () => {
            logImageError(app);
          };
        } catch (error) {
          logImageError(app, error);
        }
      });

      hasPreloaded.current = true;
    }
  }, [apps, logEvent, logImageError, getSourceImage]);
}
